// PrecoCard.js — Cartão de preço/compra da página de produto (tema escuro, conforme Figma).
// Toggle Livro Físico / Ebook, preço grande, linha do PIX (único meio de pagamento
// do site), selo de desconto (quando o servidor manda `preco_com_desconto`), cálculo de
// CEP/frete e "Comprar Agora".
// Quando o livro tem "Redirecionar Livro Físico" ligado no painel ADM, o formato
// físico não entra no carrinho: vira um botão que leva à loja externa.
//
// "VOCÊ JÁ POSSUI" (20/08/2026): se o e-book já está na conta da pessoa, o
// cartão diz isso e troca "COMPRAR AGORA" por um caminho para a biblioteca.
// Comprar o mesmo e-book duas vezes é dinheiro jogado fora, e o site não tem
// devolução automática — deixar o botão de compra ali seria deixar uma
// armadilha. O aviso vale SÓ para o digital: ter o e-book não é ter o
// impresso, então o formato físico continua comprável normalmente.
//
// E-BOOK DE PRESENTE: onde o título tem arquivo e preço de impresso, comprar o
// físico dá também a LEITURA do e-book no site. Isso aparece aqui em dois
// lugares — a plaquinha na opção "Livro Físico" e o bloco explicativo no corpo
// do cartão.
// CÓPIA DIGITAL GRÁTIS (16/09/2026): quando o painel libera o título, o
// e-book PARA DE SER VENDIDO nesta tela. O preço some, "COMPRAR AGORA" vira
// "LER AGORA" + "BAIXAR PDF", e quem ainda não entrou vê o convite para entrar
// — o arquivo mora atrás do login, que é o que mantém a trilha de quem baixou.
// O formato FÍSICO segue intacto: liberar o arquivo não é dar o papel.
//
// A armadilha, e a razão de o brinde NÃO tocar em `possuiSelecionado`: aquele
// estado troca "COMPRAR AGORA" por "LER AGORA". Se o brinde o acionasse, o
// livro que a pessoa só pode LER deixaria de ser vendável — o e-book em si,
// que é a venda que ainda sobra depois do impresso, sumiria da loja.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import AdicionarAoCarrinhoBtn from "../AdicionarAoCarrinhoBtn";
import FreteDoLivro from "./FreteDoLivro";
import { baixarPdfDoLivro } from "../../../utils/baixarPdf";

const main = process.env.REACT_APP_MAIN;

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PrecoCard = ({
  ebook, opcaoSelecionada, onOpcaoChange, quantidade,
  hasPrecoFisico, hasPrecoDigital, precoFisico, precoDigital, precoComDesconto,
  jaPossuiEbook = false,
  assinante = false,
  ebookId = null,
  temEbookBrinde = false,
  // O título foi liberado pela editora: quem decide é o servidor
  // (`copia_gratis_ativa`, que já exige o PDF existir).
  copiaGratis = false,
  // O frete vem de fora (useFreteDoLivro, criado na PaginaProduto): no
  // computador ele é desenhado embaixo da capa, e aqui dentro só no celular.
  // O ESTADO É O MESMO nos dois lugares — ver o cabeçalho do hook.
  frete,
  // `true` só quando o frete NÃO está sendo desenhado embaixo da capa.
  freteNoCartao = true,
}) => {
  // Estado do botão de baixar: um pedido de cada vez, e o erro dito na tela em
  // vez de um alerta do navegador.
  const [baixando, setBaixando] = useState(false);
  const [erroDownload, setErroDownload] = useState("");

  const formato = opcaoSelecionada || (hasPrecoFisico ? "fisico" : "ebook");
  const unico = (hasPrecoFisico && !hasPrecoDigital) || (!hasPrecoFisico && hasPrecoDigital);
  const precoBase = formato === "fisico" ? precoFisico : precoDigital;

  // Livro físico vendido fora do site (Amazon, Clube de Autores…)
  const linkFisico = (ebook?.link_fisico || "").trim();
  const fisicoRedirecionado = !!(ebook?.redirecionar_fisico && linkFisico);
  const redirecionandoAgora = fisicoRedirecionado && formato === "fisico";

  // O desconto vem PRONTO do servidor (App\Services\PrecoService) e vale só para
  // o e-book. Este cartão não calcula percentual nenhum sobre o preço: se ele
  // calculasse, o valor daqui e o que o checkout aceita divergiriam no primeiro
  // arredondamento — e um centavo de diferença derruba o pedido.
  const temDesconto = formato === "ebook" && precoComDesconto != null && precoComDesconto > 0 && precoComDesconto < precoBase;
  const precoFinal = temDesconto ? precoComDesconto : precoBase;
  const pctOff = temDesconto ? Math.round((1 - precoFinal / precoBase) * 100) : 0;

  // ===== O DESCONTO DO E-BOOK NÃO DEPENDE DO FORMATO SELECIONADO =====
  //
  // `temDesconto`, acima, responde "o preço GRANDE está com desconto?" — e por
  // isso carrega o `formato === "ebook"`. A coluna de opções faz outra pergunta:
  // "quanto custa o e-book, esteja ele escolhido ou não". Eram a mesma variável,
  // e o resultado era a tela mostrando DOIS PREÇOS DIFERENTES PARA O MESMO
  // ITEM: "Ebook R$ 30,00" na opção da esquerda e "R$ 27,00" no bloco da
  // direita, ao mesmo tempo, com o selo de 10% OFF entre os dois.
  //
  // Quem tem razão é o R$ 27,00: ele vem do servidor (PrecoService) e é o que o
  // checkout aceita. A opção mostrava `precoDigital` cru, que é o preço de
  // tabela — o número que o desconto existe para riscar.
  const ebookComDesconto =
    precoComDesconto != null && precoComDesconto > 0 && precoComDesconto < precoDigital;
  const precoDigitalFinal = ebookComDesconto ? precoComDesconto : precoDigital;

  const totalFisico = formato === "fisico" ? precoFinal * (quantidade || 1) : precoFinal;

  // Os números do Clube viajam com o livro (ver EbookController::getEbookById):
  // o endpoint `clube/status` exige login, e quem precisa ver a oferta é
  // justamente quem ainda não entrou.
  const precoClube = Number(ebook?.clube?.preco_centavos) || 0;
  const diasClube = Number(ebook?.clube?.dias) || 30;
  const acervoClube = Number(ebook?.clube?.acervo_total) || 0;
  // Zero quando o clube ainda tem poucos assinantes: o piso é do servidor, e
  // por isso a tela pode simplesmente confiar no número que chegou.
  const assinantesClube = Number(ebook?.clube?.assinantes) || 0;
  // Sempre com centavos, mesmo em valor redondo: a frase da oferta é a que o
  // cliente aprovou ("R$ 50,00/mês"), e um "R$ 50" ao lado do "R$ 40,00" do
  // preço avulso faz o Clube parecer arredondamento de propaganda em vez de
  // preço. O custo é um "00" a mais na tela.
  const fmtClube = (c) => `R$ ${fmt(c / 100)}`;
  // "/mês" só quando o ciclo do servidor é de fato de 30 dias. A frase pedida
  // diz "/mês"; se um dia a assinatura passar a valer 45 ou 90 dias, ela
  // mentiria — e mentiria exatamente onde a pessoa decide pagar. Fora dos 30
  // dias a frase perde o aperto e ganha a verdade.
  const periodoClube = diasClube === 30 ? "/mês" : ` a cada ${diasClube} dias`;

  // O e-book selecionado já está na conta: o cartão deixa de vender e passa a
  // levar para a biblioteca. `jaPossuiEbook` é PROPRIEDADE (origem "compra"),
  // nunca o brinde do impresso — ver o cabeçalho do arquivo.
  const possuiSelecionado = jaPossuiEbook && formato === "ebook";

  // ===== O DIGITAL LIBERADO =====
  //
  // `gratisSelecionado` é a pergunta que TROCA A TELA: o preço grande some, o
  // PIX some, o carrinho some. Ela só vale no e-book — liberar o arquivo não
  // torna o papel de graça, e o cartão continua vendendo o impresso ao lado.
  //
  // A ORDEM IMPORTA: quem já COMPROU o e-book continua caindo no ramo de
  // "você já possui", que fala de uma conta com o livro dentro. Dizer "grátis"
  // para quem pagou seria, no mínimo, de mau gosto.
  const gratisSelecionado = copiaGratis && formato === "ebook" && !possuiSelecionado;

  // O arquivo mora atrás do login (é assim que existe a trilha de quem baixou
  // o quê). Sem conta, o botão vira convite para entrar em vez de uma recusa
  // do servidor depois do clique.
  const temConta = !!(typeof window !== "undefined"
    && localStorage.getItem("token" + (process.env.REACT_APP_USER_FUNCTION || "")));

  const baixarAgora = async () => {
    if (baixando) return;
    setBaixando(true);
    setErroDownload("");
    try {
      await baixarPdfDoLivro({ id: ebookId || ebook?.id, titulo: ebook?.titulo });
    } catch (e) {
      setErroDownload(e?.message || "Não foi possível baixar agora.");
    } finally {
      setBaixando(false);
    }
  };

  // O presente só é anunciado onde ele de fato acontece:
  //
  //  - `temEbookBrinde` já chega com a condição do servidor (título com PDF e
  //    com preço de impresso). Prometer o brinde num título sem arquivo é a
  //    pior falha possível: a pessoa compra o impresso pela promessa e recebe
  //    um 404;
  //  - com `redirecionar_fisico` a venda do impresso acontece na loja
  //    parceira, e o site NÃO fica sabendo dela. O brinde nasce de um pedido
  //    pago aqui dentro; ali ele nunca chegaria.
  const mostrarBrinde = temEbookBrinde && hasPrecoFisico && !fisicoRedirecionado;

  const itemCarrinho = {
    id: ebook.id, titulo: ebook.titulo, image: ebook.image, autores: ebook.autores,
    preco: precoFinal,
    ...(temDesconto ? { preco_original: precoBase } : {}),
    formato, quantidade: quantidade || 1,
    // Nota e autor viajam com o item para o carrinho não precisar buscá-los.
    avaliacoes_avg_nota: ebook.avaliacoes_avg_nota,
    avaliacoes_count: ebook.avaliacoes_count,
    // Viaja com o item para o CARRINHO poder repetir a promessa sem buscar o
    // livro de novo: este cartão é o único lugar do site que vende impresso, e
    // portanto o único que sabe, na hora de adicionar, se aquele item carrega
    // o presente. (O checkout monta o pedido com id/preço/quantidade/formato;
    // campo a mais aqui não vai para a API.)
    ...(formato === "fisico" && mostrarBrinde ? { ebook_de_brinde: true } : {}),
    ...(formato === "fisico" && frete?.paraOCarrinho()
      ? { frete: frete.paraOCarrinho() }
      : {}),
  };

  const validarFrete = () => (formato === "fisico" ? frete.validar() : true);
  const trocarFormato = (f) => { onOpcaoChange && onOpcaoChange(f); frete.limpar(); };

  // Plaquinha do presente. É a MESMA em dois lugares (a opção "Livro Físico" e
  // o título do cartão quando o livro só tem um formato), então mora numa
  // função só: duas cópias divergiriam na primeira mudança de texto.
  //
  // ELA JÁ SUMIU UMA VEZ, em 03/09/2026: a reescrita do cartão para o desconto
  // de 10% trocou o bloco das plaquinhas e deixou esta de fora. O componente
  // continuou definido e o pedido continuou saindo com `ebook_de_brinde`, então
  // nada quebrou — só parou de ser anunciado o argumento que faz o impresso
  // valer mais que o digital. Quem achou foi o aviso de variável sem uso do
  // build; sem ele, a perda seria invisível.
  //
  // A FORMA vem de `.selo` (App.css), não daqui. Foi este selo que o cliente
  // apontou: com `rounded-full` ele virava uma pílula e destoava de todo o
  // resto. Só a cor âmbar continua sendo escolha deste arquivo.
  // `className` existe para o LADRILHO: no celular ele tem ~150px, e o `.selo`
  // não quebra linha (`white-space: nowrap` no App.css, fora de camada — por isso
  // o `!`). O print de 390px mostrou "E-BOOK DE PRESEN" decepado na borda.
  const PlaquinhaBrinde = ({ className = "" }) => (
    <span className={"selo border border-amber-300/40 bg-amber-300/10 text-amber-200 " + className}>
      <i className="bi bi-gift-fill" /> E-book de presente
    </span>
  );

  const Opcao = ({ value, label, preco, precoOriginal = null }) => {
    const ativo = formato === value;
    const externo = value === "fisico" && fisicoRedirecionado;
    const possui = value === "ebook" && jaPossuiEbook;
    const brinde = value === "fisico" && mostrarBrinde;
    const liberado = value === "ebook" && copiaGratis && !possui;
    return (
      // ===== O LADRILHO (17/09/2026) =====
      //
      // As opções eram uma coluna de 42% à esquerda, e o print da página
      // mostrou o estrago: "Livro / Físico" quebrado em duas linhas, a
      // plaquinha do presente espremida ao lado, e a coluna vazia embaixo da
      // segunda opção. A causa era o `px-5` — o Bootstrap declara a mesma
      // classe com !important e 3rem, e 48px de cada lado numa coluna de 230px
      // sobravam 130px para o texto.
      //
      // Agora as duas opções são LADRILHOS lado a lado, em cima do preço, com
      // espaçamentos que o Bootstrap não tem homônimo (`px-3.5`, `py-3`). O
      // selecionado ganha a borda laranja — a mesma pista de "é este" que o
      // resto do site usa — em vez de só um fundo um tom mais claro.
      <button
        type="button"
        onClick={() => trocarFormato(value)}
        aria-pressed={ativo}
        className={
          "foco-laranja flex h-full w-full items-start gap-2.5 rounded-card border-2 px-3.5 py-3 text-left transition-colors " +
          (ativo
            ? "border-insigne-orange bg-insigne-orange/[0.08]"
            : "border-insigne-sombra bg-insigne-navy-deep hover:border-white/25")
        }
      >
        {/* O anel desmarcado era `border-line`, que no tema escuro some no
            fundo: a opção não selecionada parecia não ter botão nenhum. */}
        <span className={"mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center " + (ativo ? "border-insigne-orange" : "border-white/35")}>
          {ativo && <span className="w-2 h-2 rounded-full bg-insigne-orange" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold text-white text-sm">{label}</span>
          {/* Mesmo motivo do corpo do cartão: o preço de quem vende fora não é
              nosso para anunciar. */}
          {externo ? (
            <span className="block text-white/70 text-sm">Ver na loja parceira</span>
          ) : liberado ? (
            // Sem "R$ 0,00": zero é um PREÇO, e preço zero lê como erro de
            // cadastro. "Ler e baixar de graça" diz o que a pessoa ganha.
            <span className="block text-insigne-promo text-sm font-bold">Ler e baixar de graça</span>
          ) : (
            <span className="block text-white/70 text-sm">
              R$ {fmt(preco)}
              {/* O preço de tabela riscado ao lado. Sem ele a opção mostraria
                  R$ 27,00 e o cliente que viu R$ 30,00 no catálogo não saberia
                  se o preço caiu ou se ele leu errado — o risco é o que conta
                  a história em um símbolo. */}
              {/* `whitespace-nowrap`: no ladrilho estreito do celular o riscado
                  quebrava entre o "R$" e o número, e o "30,00" caía sozinho na
                  linha de baixo. Inteiro, ele desce como uma peça só. */}
              {precoOriginal != null && (
                <span className="ml-1.5 whitespace-nowrap text-xs text-white/35 line-through">
                  R$ {fmt(precoOriginal)}
                </span>
              )}
            </span>
          )}
          {externo && <span className="block text-white/45 text-xs mt-0.5">Loja externa</span>}
          {/* As plaquinhas moram NA OPÇÃO, e não só no corpo do cartão, para a
              pessoa ver o aviso mesmo com a outra opção selecionada. Descem
              para uma linha própria: ao lado do nome, num ladrilho de 250px,
              elas quebravam o nome do formato. */}
          {(possui || brinde || liberado) && (
            <span className="mt-2 flex flex-wrap gap-1">
              {possui && (
                <span className="selo border border-emerald-400/40 bg-emerald-400/10 text-emerald-300">
                  <i className="bi bi-check-circle-fill" /> Você já possui
                </span>
              )}
              {brinde && <PlaquinhaBrinde className="!whitespace-normal text-left" />}
              {liberado && (
                <span className="selo bg-insigne-promo text-ink-fixed">
                  <i className="bi bi-unlock-fill" /> Grátis
                </span>
              )}
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    // `.cartao` (App.css): borda de 2px na cor da sombra e a sombra dura de
    // 4px. Aqui estava a receita escrita à mão — `rounded-xl` mais o fundo
    // navy-soft, sem borda e sem sombra —, e o resultado era o objeto que
    // fecha a venda parecendo mais leve que os blocos de texto da página.
    // Ele é a ÚNICA peça com sombra no hero, e é isso que o põe à frente.
    <div className="cartao overflow-hidden flex flex-col bg-insigne-navy-mid">
      {/* Os ladrilhos das opções (só com 2 formatos), em cima do preço. O
          `pb-0` do Bootstrap é 0 com !important, igual ao do Tailwind — aqui
          a colisão não muda nada, e o respiro de baixo vem do bloco do preço. */}
      {!unico && (
        <div className="grid grid-cols-2 gap-2.5 px-3.5 pt-3.5 sm:px-6 sm:pt-6 md:px-7 md:pt-7">
          {hasPrecoFisico && <Opcao value="fisico" label="Livro Físico" preco={precoFisico} />}
          {hasPrecoDigital && (
            <Opcao
              value="ebook"
              label="Ebook"
              preco={precoDigitalFinal}
              precoOriginal={ebookComDesconto ? precoDigital : null}
            />
          )}
        </div>
      )}

      {/* Coluna de preço e ações */}
      <div className="flex-1 p-6 md:p-7 flex flex-col">
        {unico && (
          <p className="flex items-center gap-2 text-white font-bold mb-2">
            {formato === "fisico" ? "Livro Físico" : "Ebook"}
            {possuiSelecionado && (
              <span className="selo border border-emerald-400/40 bg-emerald-400/10 text-emerald-300">
                <i className="bi bi-check-circle-fill" /> Você já possui
              </span>
            )}
            {formato === "fisico" && mostrarBrinde && <PlaquinhaBrinde />}
            {gratisSelecionado && (
              <span className="selo bg-insigne-promo text-ink-fixed">
                <i className="bi bi-unlock-fill" /> Liberado pela editora
              </span>
            )}
          </p>
        )}

        {/* O desconto tem de ser VISTO, não encontrado. Antes era uma plaquinha
            verde-água discreta ao lado de um preço riscado minúsculo; num cartão
            cujo preço tem 44px, isso desaparece. Ele passou por um período em
            LARANJA — que resolveu o tamanho e criou outro problema: laranja é a
            cor de "olhe aqui" em todo o site, e nesta tela ela já está no botão
            de comprar, na oferta do Clube e no preço. Mais um laranja não
            destacava nada; só somava ruído.
            O verde (`--c-promo`) foi escolhido pelo dono. É o MESMO selo do
            card da home — chapado, texto escuro, mesmo raio — para o mesmo
            fato ser o mesmo objeto nos dois lugares em que ele aparece.

            O TEXTO É ESCURO, e não branco. Branco sobre este verde dá 2,2:1 no
            tema escuro, que é o PADRÃO do site: o selo ficaria menos legível
            que o laranja que ele substituiu (4,1:1) — o elemento que existe
            para destacar sairia pior do que estava. `text-ink-fixed` sobre o
            mesmo verde dá 4,88:1 e passa AA nos dois temas, sem mexer na cor
            que o dono pediu.

            AINDA HÁ VERDE DE ESTADO NESTA TELA: "Você já possui" é emerald e
            aparece a poucos pixels daqui quando o livro comprado está em
            promoção. Os dois não se confundem por serem de tom e desenho
            diferentes (aquele translúcido com texto claro, este chapado com
            texto escuro), mas são dois verdes vizinhos dizendo coisas
            diferentes — se a dupla incomodar, quem muda é o de estado.
            "Economize R$ 3,00" continua em reais porque porcentagem exige
            conta e real não. */}
        {temDesconto && (
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-selo bg-insigne-promo text-ink-fixed text-sm font-extrabold uppercase tracking-wide">
              <i className="bi bi-tag-fill" /> {pctOff}% OFF
            </span>
            <span className="text-white/50 line-through text-base">R$ {fmt(precoBase)}</span>
            <span className="text-insigne-promo text-sm font-bold">
              economize R$ {fmt(precoBase - precoFinal)}
            </span>
          </div>
        )}

        {/* VENDA EM LOJA EXTERNA NÃO MOSTRA PREÇO, e isso é de propósito.
            O valor de `preco_impresso` é o que está cadastrado aqui, mas quem
            cobra é a outra loja — e ela muda o preço quando quiser, sem avisar
            este site. Um número grande em cima de um botão que leva para fora
            é uma promessa que a editora não tem como cumprir: a pessoa clica
            esperando R$ 60 e encontra R$ 79.
            Melhor não dizer do que dizer errado. */}
        {gratisSelecionado ? (
          // ===== O LUGAR DO PREÇO, QUANDO NÃO HÁ PREÇO =====
          //
          // "R$ 0,00" não entra aqui. Zero é um preço, e preço zero num
          // catálogo lê como cadastro pela metade — o cliente desconfia do
          // livro em vez de baixá-lo. A palavra faz o trabalho que o número
          // não faz, e fica no mesmo corpo do preço dos vizinhos para o
          // cartão não mudar de peso quando o título é liberado.
          <div className="mb-4">
            <p className="text-[40px] md:text-[44px] leading-none font-extrabold text-insigne-promo">
              Grátis
            </p>
            <p className="text-white/70 text-sm mt-2.5 leading-relaxed">
              A editora liberou a versão digital deste livro. Leia aqui no site ou baixe o
              PDF, sem pagar nada, quantas vezes quiser.
            </p>
          </div>
        ) : redirecionandoAgora ? (
          <div className="mb-4">
            <p className="text-xl font-bold text-white leading-tight">
              Vendido em loja parceira
            </p>
            <p className="text-white/55 text-sm mt-1.5 leading-relaxed">
              O preço e a entrega do impresso são da loja parceira, e podem mudar
              sem aviso. Confira lá antes de finalizar.
            </p>
          </div>
        ) : (
          <>
            <div className="text-[40px] md:text-[44px] leading-none font-extrabold text-white mb-3">
              R${fmt(formato === "fisico" ? totalFisico : precoFinal)}
            </div>

            {/* Pagamento — o site aceita somente PIX */}
            <div className="space-y-1.5 mb-4 text-sm">
              <p className="flex items-center gap-2 text-white/80">
                <i className="bi bi-qr-code text-white/60" /> R$ {fmt(formato === "fisico" ? totalFisico : precoFinal)} à vista no PIX
              </p>
              <p className="text-white/50 text-xs">Pagamento exclusivamente via PIX, com QR Code gerado na finalização.</p>
            </div>
          </>
        )}

        {/* Quantidade + frete (livro físico vendido aqui) */}
        {formato === "fisico" && !redirecionandoAgora && (
          <>
            <div className="flex items-center gap-3 mb-3 text-sm">
              <span className="text-white/60">Quantidade:</span>
              <div className="inline-flex items-center rounded-controle border border-line overflow-hidden">
                <button onClick={() => onOpcaoChange && onOpcaoChange(formato, Math.max(1, (quantidade || 1) - 1))} disabled={(quantidade || 1) <= 1} className="w-8 h-8 flex items-center justify-center text-white/80 hover:bg-white/10 disabled:opacity-30">−</button>
                <span className="min-w-8 text-center font-bold text-white">{quantidade || 1}</span>
                <button onClick={() => onOpcaoChange && onOpcaoChange(formato, Math.min(99, (quantidade || 1) + 1))} disabled={(quantidade || 1) >= 99} className="w-8 h-8 flex items-center justify-center text-white/80 hover:bg-white/10 disabled:opacity-30">+</button>
              </div>
            </div>

            {/* Do computador para cima este mesmo frete é desenhado embaixo
                da capa, onde sobrava um vão (pedido do dono em 18/09/2026, com
                o print) — e então ele nem é montado aqui. Um `lg:hidden` teria
                deixado dois campos de CEP e dois grupos de rádio no documento,
                que é um defeito de verdade: ver useTelaGrande.js. */}
            {freteNoCartao && <FreteDoLivro frete={frete} className="mb-3" />}
          </>
        )}

        {/* Assinante do Clube: o livro já está liberado para leitura. O cartão
            continua vendendo (comprar dá o PDF e o acesso permanente, que a
            assinatura não dá), mas dizer isso DEPOIS da compra seria vender a
            alguém o que ela já paga. */}
        {assinante && !possuiSelecionado && !gratisSelecionado && formato === "ebook" && (
          <Link
            to={main + "ler/" + (ebookId || ebook?.id)}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-4 flex items-center justify-center gap-2 rounded-controle border border-insigne-orange/50
                       bg-insigne-orange/10 py-3 font-bold text-insigne-orange
                       hover:bg-insigne-orange/20 transition-colors"
          >
            <i className="bi bi-stars" /> LER COM O CLUBE INSIGNE
          </Link>
        )}

        {/* ============ A OFERTA DO CLUBE, PARA QUEM AINDA NÃO ASSINA ============
            Até aqui, a página do livro só falava do Clube para quem JÁ assinava
            (o botão "LER COM O CLUBE" logo acima). A oferta aparecia exatamente
            para quem já tinha comprado e sumia para quem não tinha.

            Este é o momento de maior intenção do site inteiro: a pessoa está
            olhando UM livro e decidindo. Dizer aqui que ele está incluso na
            assinatura custa uma caixa; não dizer custa a assinatura.

            Não aparece para o impresso: a assinatura dá leitura, não papel, e
            oferecer o Clube em cima do físico prometeria o que ela não entrega. */}
        {/* `!gratisSelecionado`: oferecer assinatura para destravar um livro
            que já está destravado é vender o que a pessoa acabou de ganhar. */}
        {!assinante && !possuiSelecionado && !gratisSelecionado && formato === "ebook" && precoClube > 0 && (
          <Link
            to={main + "clube"}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-4 block rounded-card border border-insigne-orange/35 bg-insigne-orange/[0.07]
                       p-3.5 hover:bg-insigne-orange/[0.12] transition-colors"
          >
            <p className="flex items-center gap-2 text-sm font-bold text-white">
              <i className="bi bi-stars text-insigne-orange" />
              Este livro está incluso no Clube Insigne
            </p>
            {/* O argumento deixou de ser "este livro e mais alguns" e passou a
                ser o catálogo inteiro: é o acervo que justifica a mensalidade,
                não o título que a pessoa já está olhando. O número de livros e
                o preço vêm do servidor (ebook.clube) — escrever "R$ 50,00" à
                mão aqui é garantir que, no dia em que a assinatura mudar de
                preço, esta tela continue vendendo o valor velho.
                A seta "Conhecer →" saiu a pedido do cliente: o cartão inteiro
                já é o link, e a seta repetia o convite com um enfeite. */}
            <p className="text-xs text-white/60 mt-1 leading-relaxed">
              Leia {acervoClube > 0 ? `todos os ${acervoClube} livros` : "todos os livros"}
              {" "}do nosso catálogo por apenas {fmtClube(precoClube)}{periodoClube}.
            </p>
            {assinantesClube > 0 && (
              <p className="text-[11px] text-white/45 mt-1.5 inline-flex items-center gap-1">
                <i className="bi bi-people-fill" />
                {assinantesClube} pessoas já leem assim.
              </p>
            )}
          </Link>
        )}

        <div className="mt-auto pt-2">
          {/* NO COMPUTADOR o campo de frete está na outra coluna, e o recado de
              "calcule o frete antes" ficaria a meia tela de distância do botão
              que o disparou. Aqui ele aparece junto do botão — e só ali, porque
              no celular o próprio campo, logo acima, já mostra o mesmo texto. */}
          {!freteNoCartao && formato === "fisico" && !redirecionandoAgora && frete?.erro && (
            <p className="mb-2 text-xs text-red-300">{frete.erro}</p>
          )}
          {possuiSelecionado ? (
            <>
              {/* LER vem antes de "ir para meus e-books": quem já tem o livro
                  e está nesta página quer abrir o livro, não navegar até uma
                  lista onde ele está. O download continua a um clique dali. */}
              <Link
                to={main + "ler/" + (ebookId || ebook?.id)}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="w-full flex items-center justify-center gap-2 rounded-controle bg-insigne-orange text-paper
                           py-3 font-bold hover:bg-insigne-orange-dark transition-colors"
              >
                <i className="bi bi-book" /> LER AGORA
              </Link>
              <Link
                to={main + "perfil/"}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  // O perfil abre na aba "Dados"; o evento leva direto para a
                  // biblioteca, que é para onde a pessoa quer ir.
                  setTimeout(() => window.dispatchEvent(
                    new CustomEvent("changePerfiltab", { detail: "meus-livros" })
                  ), 120);
                }}
                className="w-full flex items-center justify-center gap-2 rounded-controle border border-line
                           text-white/80 py-2.5 mt-2 text-sm font-semibold hover:text-white
                           hover:border-white/30 transition-colors"
              >
                <i className="bi bi-collection-play" /> Ver meus e-books
              </Link>
              <p className="text-white/50 text-xs mt-2 text-center">
                Este e-book já está na sua conta: leia quantas vezes quiser, sem pagar de novo.
                {hasPrecoFisico && " Para levar o impresso, escolha \"Livro Físico\" acima."}
              </p>
            </>
          ) : gratisSelecionado ? (
            // ===== LER E BAIXAR, NESTA ORDEM =====
            //
            // Ler vem primeiro porque é o que acontece sem sair da página e
            // sem esperar 30 MB descerem. Baixar é o segundo caminho, e não um
            // caminho menor: "cópia digital grátis" promete o ARQUIVO, e um
            // livro que só se pode ler no navegador não é isso.
            //
            // SEM CONTA, NENHUM DOS DOIS. O arquivo está atrás do login, e é
            // assim que sobra registro de quem baixou o quê — a única coisa
            // que responde essa pergunta se um PDF aparecer circulando. O
            // convite diz isso na cara, em vez de deixar a pessoa clicar e
            // levar uma recusa.
            temConta ? (
              <>
                <Link
                  to={main + "ler/" + (ebookId || ebook?.id)}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="w-full flex items-center justify-center gap-2 rounded-controle bg-insigne-orange text-paper
                             py-3 font-bold hover:bg-insigne-orange-dark transition-colors"
                >
                  <i className="bi bi-book" /> LER AGORA
                </Link>
                <button
                  onClick={baixarAgora}
                  disabled={baixando}
                  className="w-full flex items-center justify-center gap-2 rounded-controle border border-line
                             text-white/80 py-2.5 mt-2 text-sm font-semibold hover:text-white
                             hover:border-white/30 transition-colors disabled:opacity-60"
                >
                  <i className={baixando ? "bi bi-arrow-repeat" : "bi bi-download"} />
                  {baixando ? "BAIXANDO…" : "BAIXAR PDF"}
                </button>
                {erroDownload && (
                  <p className="text-red-300 text-xs mt-2 text-center">{erroDownload}</p>
                )}
                {hasPrecoFisico && (
                  <p className="text-white/50 text-xs mt-2 text-center">
                    O livro impresso continua à venda. Escolha "Livro Físico" acima.
                  </p>
                )}
              </>
            ) : (
              <>
                <Link
                  to={main + "login"}
                  state={{ de: window.location.pathname }}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="w-full flex items-center justify-center gap-2 rounded-controle bg-insigne-orange text-paper
                             py-3 font-bold hover:bg-insigne-orange-dark transition-colors"
                >
                  <i className="bi bi-box-arrow-in-right" /> ENTRAR PARA LER GRÁTIS
                </Link>
                <p className="text-white/50 text-xs mt-2 text-center">
                  A conta é gratuita e leva um minuto. Depois de entrar você volta para esta
                  página e o livro abre direto.
                </p>
              </>
            )
          ) : redirecionandoAgora ? (
            <>
              <a
                href={linkFisico}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 rounded-controle bg-insigne-orange text-paper
                           py-3 font-bold hover:bg-insigne-orange-dark transition-colors"
              >
                <i className="bi bi-box-arrow-up-right" /> COMPRAR LIVRO FÍSICO
              </a>
              <p className="text-white/50 text-xs mt-2 text-center">
                A versão impressa é vendida em loja parceira. Você será levado para o site dela.
              </p>
            </>
          ) : (
            <AdicionarAoCarrinhoBtn btnText="COMPRAR AGORA" newBtn ebook={itemCarrinho} onBeforeAdd={() => {
              if (!precoFinal || precoFinal <= 0) { alert("Preço inválido para este livro."); return false; }
              return formato === "fisico" ? validarFrete() : true;
            }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PrecoCard;
